import React, { useCallback, useEffect, useRef } from 'react';
import Popup from 'reactjs-popup';
import styled, { keyframes } from 'styled-components';

import { softCloseHover } from '@components/common/dialog/softCloseHover';
import MaterialIcon from '@components/common/icons/MaterialIcon';
import { newEstimateModalStyle, popupEdgeCSS } from '@utils/popupUtils';
import { prefersLessMotion } from '@utils/calendar/markUsedCelebration';
import { DISMISS_HOLD_MS } from '@utils/askInPopup';

/** How long the question's overlay takes to fade in and out (Petr, 2026-10-31: 0.2 s rather than a one-frame snap). */
export const QUESTION_FADE_MS = 200;

type Props = {
  open: boolean;
  // The answer the × and a click on the dim give: "No" (Petr, 2026-10-31). Esc does nothing.
  onDismiss: () => void;
  dismissLabel: string;
  children: (leave: (then: () => void) => void) => React.ReactNode;
};

/**
 * The overlay an estimate question opens as where no session popup is open to ask it (askInPopup): it fades in, rising
 * a few pixels, and fades out before what was answered is saved. The × and a click on the dim answer "No".
 */
const QuestionDialog = ({ open, onDismiss, dismissLabel, children }: Props) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const leaving = useRef(false);
  const openedAt = useRef(0);

  useEffect(() => {
    if (!open) return;
    leaving.current = false;
    openedAt.current = Date.now();
  }, [open]);

  // Fades out, then `then` answers it. Once only: a second click while it fades does nothing.
  const leave = useCallback((then: () => void) => {
    if (leaving.current) return;
    leaving.current = true;
    const content = contentRef.current?.closest<HTMLElement>('.popup-content');
    const overlay = content?.parentElement;
    if (!content || prefersLessMotion() || typeof content.animate !== 'function') {
      then();
      return;
    }
    const out = { duration: QUESTION_FADE_MS, easing: 'ease-in', fill: 'forwards' as const };
    overlay?.animate([{ opacity: 1 }, { opacity: 0 }], out);
    content
      .animate(
        [
          { opacity: 1, transform: 'none' },
          { opacity: 0, transform: 'translateY(6px)' }
        ],
        out
      )
      .finished.then(then, then);
  }, []);

  // A click on the dim, not on the box: "No", once the box has been there a moment (DISMISS_HOLD_MS). The library's own
  // close on a document click would skip the fade.
  useEffect(() => {
    if (!open) return;
    const overlay = contentRef.current?.closest('.popup-content')?.parentElement;
    if (!overlay) return;
    const onClick = (event: MouseEvent) => {
      if (event.target !== overlay || Date.now() - openedAt.current < QUESTION_FADE_MS + DISMISS_HOLD_MS) return;
      leave(onDismiss);
    };
    overlay.addEventListener('click', onClick);
    return () => overlay.removeEventListener('click', onClick);
  });

  return (
    <Dialog open={open} modal closeOnDocumentClick={false} closeOnEscape={false} contentStyle={newEstimateModalStyle}>
      <Content ref={contentRef}>
        <CloseButton name={'close'} size={20} onClick={() => leave(onDismiss)} aria-label={dismissLabel} />
        {children(leave)}
      </Content>
    </Dialog>
  );
};

export default QuestionDialog;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const riseIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: none; }
`;

const Dialog = styled(Popup)`
  &-content {
    background: ${({ theme }) => theme.background} !important;
    ${popupEdgeCSS};
    overflow: hidden;
    animation: ${riseIn} ${QUESTION_FADE_MS}ms ease-out;
  }

  &-overlay {
    background: ${({ theme }) => theme.modalOverlayColor} !important;
    animation: ${fadeIn} ${QUESTION_FADE_MS}ms ease-out;
  }

  @media (prefers-reduced-motion: reduce) {
    &-content,
    &-overlay {
      animation: none;
    }
  }
`;

const Content = styled.div`
  position: relative;
  width: 100%;
`;

// In the box's top-right corner, past its side padding.
const CloseButton = styled(MaterialIcon)`
  position: absolute;
  top: -8px;
  right: -33px;
  cursor: pointer;
  color: ${({ theme }) => theme.textColor};
  ${softCloseHover()}
`;
